import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { History, RestartAlt, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../shared/Navbar';
import OverflowTooltip from '../../shared/OverflowTooltip';
import BreakdownStepper from './BreakdownStepper';
import ACGenerator from './ACGenerator';
import TaskGeneratorStepper from './TaskGeneratorStepper';
import AgentReportViewer from './AgentReportViewer';
import StrategistHistoryView from './StrategistHistoryView';
import { fetchProjectDropdownsByUser } from '../../store/ticketSlice';
import { clearBreakdown } from '../../store/strategistSlice';
import { COLORS } from '../../shared/colors';
import './StrategistAgent.scss';

const STRATEGIST_GENERATION_TABS = [0, 1, 2];
const STRATEGIST_JOB_TYPES = new Set([
  'BREAKDOWN',
  'TASK_GENERATION',
  'TASK_GENERATOR',
  'AC_GENERATION',
  'ACCEPTANCE_CRITERIA',
  'ACCEPTANCE_CRITERIA_GENERATION'
]);

const StrategistAgentDashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { projectDropdownsByUser: projectsData, projectDropdownsByUserLoading: projectsLoading } = useSelector((state) => state.tickets);
  const { accessToken, user } = useSelector((state) => state.auth);

  const projects = useMemo(() => {
    if (!projectsData) return [];
    if (Array.isArray(projectsData)) return projectsData;
    if (typeof projectsData === 'object') {
      const values = Object.values(projectsData);
      if (values.length > 0 && Array.isArray(values[0])) {
        return values[0];
      }
      return values;
    }
    return [];
  }, [projectsData]);

  const [activeTab, setActiveTab] = useState(() => {
    const saved = sessionStorage.getItem('strategistActiveTab');
    return saved !== null ? Number(saved) : 0;
  });
  const [acGeneratorTaskId, setAcGeneratorTaskId] = useState(null);
  const [acGeneratorTaskNumber, setAcGeneratorTaskNumber] = useState(null);
  const [acGeneratorTaskTitle, setAcGeneratorTaskTitle] = useState(null);
  const [projectId, setProjectId] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const hasAutoSelectedProject = useRef(false);

  useEffect(() => {
    dispatch(fetchProjectDropdownsByUser());
  }, [dispatch]);

  useEffect(() => {
    if (projects.length > 0 && !hasAutoSelectedProject.current) {
      const firstProject = projects[0];
      const projId = firstProject.id || firstProject._id || firstProject.project_id;
      setProjectId(projId);
      hasAutoSelectedProject.current = true;
    }
  }, [projects]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    sessionStorage.setItem('strategistActiveTab', newValue);
  };

  const handleCreateAC = (ticketId, ticketNumber, ticketTitle) => {
    setAcGeneratorTaskId(ticketId);
    setAcGeneratorTaskNumber(ticketNumber);
    setAcGeneratorTaskTitle(ticketTitle);
    setActiveTab(1);
  };

  const handleReset = () => {
    if (!showHistory) {
      dispatch(clearBreakdown());
      setAcGeneratorTaskId(null);
      setAcGeneratorTaskNumber(null);
      setAcGeneratorTaskTitle(null);
      setResetTrigger(prev => prev + 1);
    }
  };

  const handleRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (showHistory) return undefined;

    const handleAiCompleted = (event) => {
      const { data } = event?.detail || {};
      const jobType = data?.jobType || data?.job_type;
      const shouldRefresh = !jobType || STRATEGIST_JOB_TYPES.has(jobType);

      if (shouldRefresh && STRATEGIST_GENERATION_TABS.includes(activeTab)) {
        handleRefresh();
      }
    };

    window.addEventListener('pmpi:ai-response-completed', handleAiCompleted);
    return () => {
      window.removeEventListener('pmpi:ai-response-completed', handleAiCompleted);
    };
  }, [activeTab, handleRefresh, showHistory]);

  const tabs = [
    { label: t('agent.tabs.breakdown'), component: 'breakdown' },
    { label: t('agent.tabs.acGenerator'), component: 'ac' },
    { label: t('agent.tabs.taskGenerator', 'Task Generator'), component: 'task' },
    { label: t('agent.tabs.reports'), component: 'reports' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <Box className="breakdown-tab">
            <BreakdownStepper
              projectId={projectId}
              onCreateAC={handleCreateAC}
              resetTrigger={resetTrigger}
              refreshTrigger={refreshTrigger}
            />
          </Box>
        );
      case 1:
        return (
          <Box className="ac-generator-tab">
            <ACGenerator
              projectId={projectId}
              taskId={acGeneratorTaskId}
              taskNumber={acGeneratorTaskNumber}
              taskTitle={acGeneratorTaskTitle}
              clearExistingAC={!!acGeneratorTaskId}
              resetTrigger={resetTrigger}
              refreshTrigger={refreshTrigger}
              headerTitle={t('agent.tabs.acGenerator', 'AC Generator')}
            />
          </Box>
        );
      case 2:
        return (
          <Box className="task-generator-tab">
            <TaskGeneratorStepper
              projectId={projectId}
              resetTrigger={resetTrigger}
              refreshTrigger={refreshTrigger}
            />
          </Box>
        );
      case 3:
        return (
          <Box className="reports-tab">
            <AgentReportViewer
              projectId={projectId}
              resetTrigger={resetTrigger}
              refreshTrigger={refreshTrigger}
            />
          </Box>
        );
      default:
        return null;
    }
  };


  if (showHistory) {
    return (
      <div className="dashboard">
        <Navbar />
        <main className="main">
          <Box className="strategist-agent-dashboard">
            <Box className="dashboard-content-wrapper">
              <StrategistHistoryView projectId={projectId} onBack={() => setShowHistory(false)} />
            </Box>
          </Box>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Navbar />

      <main className="main">
        <Box className="strategist-agent-dashboard">
          <Box className="dashboard-content-wrapper">
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1, borderRadius: '8px' }}>
              <Typography variant="h6" fontWeight={600} color={COLORS.text.muted}>
                {t('agent.title')}
              </Typography>
            </Box>
            <Box className="project-selector-container">
              <FormControl className="project-select-form-control" disabled={projectsLoading}>
                <InputLabel id="project-select-label">{t('agent.selectProject')}</InputLabel>
                <Select
                  labelId="project-select-label"
                  value={projectId || ''}
                  onChange={(e) => {
                    setProjectId(e.target.value);
                  }}
                  label={t('agent.selectProject')}
                  renderValue={(value) => {
                    if (!value) return '';
                    const selectedProject = projects.find(
                      (p) => (p.id || p._id || p.project_id) === value
                    );
                    const projectName = selectedProject
                      ? selectedProject.name || selectedProject.title || `Project ${value}`
                      : value;
                    return (
                      <OverflowTooltip
                        title={projectName}
                        tooltipWidth={350}
                        component={Box}
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%'
                        }}
                        placement="top-start"
                      >
                        {projectName}
                      </OverflowTooltip>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      className: 'project-select-menu-paper',
                      style: {
                        width: 200,
                        maxHeight: 200,
                        overflow: 'auto'
                      }
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                >
                  {projectsLoading ? (
                    <MenuItem disabled value="">
                      <Box className="project-select-loading">
                        <CircularProgress size={16} />
                        <span>Loading projects...</span>
                      </Box>
                    </MenuItem>
                  ) : projects.length === 0 ? (
                    <MenuItem disabled value="">
                      <em>No projects available</em>
                    </MenuItem>
                  ) : (
                    projects.map((project) => {
                      const projId = project.id || project._id || project.project_id;
                      const projectName = project.name || project.title || `Project ${projId}`;
                      return (
                        <MenuItem key={projId} value={projId} className="project-select-menu-item">
                          <OverflowTooltip title={projectName} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
              </FormControl>
              <Tooltip
                title={t('agent.history.button')}
                arrow
                slotProps={{ tooltip: { className: 'app-tooltip' } }}
              >
                <IconButton
                  onClick={() => setShowHistory(true)}
                  className="history-icon-button"
                  aria-label={t('agent.history.button')}
                >
                  <History />
                </IconButton>
              </Tooltip>

              <Tooltip
                title={t('agent.reset.button')}
                arrow
                slotProps={{ tooltip: { className: 'app-tooltip' } }}
              >
                <IconButton
                  onClick={handleReset}
                  className="reset-icon-button"
                  aria-label={t('agent.reset.button')}
                >
                  <RestartAlt />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={t('agent.refresh.button')}
                arrow
                slotProps={{ tooltip: { className: 'app-tooltip' } }}
              >
                <IconButton
                  onClick={handleRefresh}
                  className="refresh-icon-button"
                  aria-label={t('agent.refresh.button')}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>

            <Box className="tabs-container">
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                className="agent-tabs"
              >
                {tabs.map((tab, index) => (
                  <Tab key={index} label={tab.label} />
                ))}
              </Tabs>
            </Box>

            <Box className="tab-content-wrapper">
              {projectId ? (
                renderTabContent()
              ) : (
                <Box className="empty-project-message">
                  <Typography variant="h6" color="text.secondary">
                    {t('agent.selectProjectFirst')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </main>

      {/* Clarification phase disabled for now, might need in future */}
      {/* <ClarificationDialog
        open={clarifications && clarifications.length > 0}
        onClose={handleCloseClarification}
        breakdownId={breakdownId}
        questions={clarifications}
        onAnswered={handleClarificationAnswered}
        projectId={projectId}
      /> */}
    </div>
  );
};

export default StrategistAgentDashboard;
